import { t } from '../locale.js'
import { allCapsShadowCss } from './allCapsShadowCss.js'

const GOOGLE_FONTS_URL = 'https://fonts.googleapis.com/css2?family=Bonbon&family=Butcherman&family=Fontdiner+Swanky&family=Fredericka+the+Great&family=Londrina+Outline&family=Love+Ya+Like+A+Sister&family=Moo+Lah+Lah&family=Mountains+of+Christmas&family=Noto+Color+Emoji&family=Noto+Music&family=Noto+Sans+Symbols+2&family=Oi&family=Ole&family=Rubik+Burned&family=Unkempt&family=Agbalumo&family=Bowlby+One+SC&family=Bungee&family=Chango&family=Cherry+Bomb+One&family=Chewy&family=Coiny&family=Concert+One&family=DynaPuff&family=Frijole&family=Indie+Flower&family=Knewave&family=Lemonada&family=Londrina+Sketch&family=Luckiest+Guy&family=Mansalva&family=Permanent+Marker&family=Poller+One&family=Potta+One&family=Rammetto+One&family=Rampart+One&family=Rubik+Scribble&family=Rubik+Vinyl&family=Sarina&family=Sedgwick+Ave+Display&family=Sigmar+One&family=Slackey&family=Titan+One&display=swap'

const template = document.createElement('template')
template.innerHTML = `${allCapsShadowCss}
<link href="${GOOGLE_FONTS_URL}" rel="stylesheet">
<style>
  :host {
    display: inline-block;
    position: relative;
  }
  .dropdown-container {
    position: relative;
    display: inline-block;
  }
  .dropdown-selected {
    background-color: var(--input-color, #fff);
    border: 1px solid #ccc;
    padding: 4px 24px 4px 8px;
    cursor: pointer;
    min-width: 120px;
    font-size: 14px;
    position: relative;
    border-radius: 3px;
  }
  .dropdown-selected::after {
    content: '▼';
    position: absolute;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    font-size: 10px;
    pointer-events: none;
  }
  .dropdown-options {
    display: none;
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: #fff;
    border: 1px solid #ccc;
    border-top: none;
    max-height: 300px;
    overflow-y: auto;
    z-index: 1000;
    box-shadow: 0 4px 8px rgba(0,0,0,0.2);
  }
  .dropdown-options.open {
    display: block;
  }
  .dropdown-option {
    padding: 8px 12px;
    cursor: pointer;
    font-size: 16px;
    border-bottom: 1px solid #eee;
  }
  .dropdown-option:hover {
    background: #e0e0ff;
  }
  .dropdown-option.selected {
    background: #d0d0ff;
  }
  label {
    margin-right: 4px;
    font-size: 12px;
  }
</style>
<label></label>
<div class="dropdown-container">
  <div class="dropdown-selected"></div>
  <div class="dropdown-options"></div>
</div>
`

export class SeFontSelect extends HTMLElement {
    constructor() {
        super()
        this._shadowRoot = this.attachShadow({ mode: 'open' })
        this._shadowRoot.append(template.content.cloneNode(true))
        this.$label = this._shadowRoot.querySelector('label')
        this.$selected = this._shadowRoot.querySelector('.dropdown-selected')
        this.$options = this._shadowRoot.querySelector('.dropdown-options')
        this._value = ''
        this._optionsList = []
        this._valuesList = []
        this._isOpen = false

        this.$selected.addEventListener('click', () => this.toggleDropdown())

        // Close dropdown when clicking outside
        document.addEventListener('click', (e) => {
            if (!this.contains(e.target)) {
                this.closeDropdown()
            }
        })
    }

    static get observedAttributes() {
        return ['label', 'options', 'values', 'disabled']
    }

    attributeChangedCallback(name, oldValue, newValue) {
        if (oldValue === newValue) return
        switch (name) {
            case 'label':
                this.$label.textContent = t(newValue)
                break
            case 'options':
                this._optionsList = newValue ? newValue.split(',') : []
                this.renderOptions()
                break
            case 'values':
                this._valuesList = newValue ? newValue.split('::') : []
                this.renderOptions()
                break
            case 'disabled':
                // Handle disabled state
                break
        }
    }

    renderOptions() {
        this.$options.innerHTML = ''

        this._optionsList.forEach((optionLabel, index) => {
            const value = this._valuesList[index] || optionLabel
            const optionEl = document.createElement('div')
            optionEl.className = 'dropdown-option'
            optionEl.textContent = t(optionLabel)
            optionEl.style.fontFamily = `"${value}", sans-serif`
            optionEl.dataset.value = value

            if (value === this._value) {
                optionEl.classList.add('selected')
            }

            optionEl.addEventListener('click', (e) => {
                e.stopPropagation()
                this.selectValue(value, t(optionLabel))
            })

            this.$options.appendChild(optionEl)
        })

        // Set initial selected display
        if (!this._value && this._valuesList.length > 0) {
            this._value = this._valuesList[0]
            this.$selected.textContent = t(this._optionsList[0])
            this.$selected.style.fontFamily = `"${this._value}", sans-serif`
        }
    }

    selectValue(value, label) {
        this._value = value
        this.$selected.textContent = label
        this.$selected.style.fontFamily = `"${value}", sans-serif`

        // Update selected state in options
        this.$options.querySelectorAll('.dropdown-option').forEach(opt => {
            opt.classList.toggle('selected', opt.dataset.value === value)
        })

        this.closeDropdown()

        // Dispatch change event
        this.dispatchEvent(new CustomEvent('change', {
            detail: { value },
            bubbles: true
        }))
    }

    toggleDropdown() {
        this._isOpen = !this._isOpen
        this.$options.classList.toggle('open', this._isOpen)
    }

    closeDropdown() {
        this._isOpen = false
        this.$options.classList.remove('open')
    }

    get value() {
        return this._value
    }

    set value(val) {
        this._value = val
        const index = this._valuesList.indexOf(val)
        if (index >= 0) {
            this.$selected.textContent = t(this._optionsList[index])
            this.$selected.style.fontFamily = `"${val}", sans-serif`
        }
    }

    connectedCallback() {
        // Ensure fonts are loaded
    }
}

customElements.define('se-font-select', SeFontSelect)
